import React, { useState, useEffect } from 'react';
import PageContainer from '../../components/common/PageContainer';
import PageSection from '../../components/common/PageSection';
import AdminNavigation from '../../components/admin/AdminNavigation';
import { useAuth } from '../../context/shared/AuthContext';
import apiService from '../../services/shared/apiService';

interface GiftRequest {
  id: string;
  userId: string;
  incomeRange: string;
  hardship?: string;
  reflectionStreak: number;
  tier: string;
  tokensAwarded: number;
  status: 'pending' | 'approved' | 'denied';
  createdAt: string;
  escalationFlag?: boolean;
  appealSubmitted?: boolean;
}

const GiftRequestReviewPage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<GiftRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<GiftRequest | null>(null);
  const [reviewNote, setReviewNote] = useState('');

  useEffect(() => {
    // Only allow admin users
    if (user && user.role === 'admin') {
      fetchPendingRequests();
    }
  }, [user]);

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPendingGiftRequests();
      setRequests(response.requests || []);
    } catch (err: any) {
      setError('Failed to fetch pending requests');
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (requestId: string, action: 'approve' | 'deny') => {
    try {
      await apiService.reviewGiftRequest(requestId, { action, note: reviewNote });
      setReviewNote('');
      setSelectedRequest(null);
      fetchPendingRequests(); // Refresh the list
    } catch (err: any) {
      setError(`Failed to ${action} request`);
      console.error(`Error ${action}ing request:`, err);
    }
  };

  const getStatusBadge = (status: string, escalationFlag?: boolean) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full';
    
    if (escalationFlag) {
      return <span className={`${baseClasses} bg-red-100 text-red-800  `}>
        ⚠️ Escalated
      </span>;
    }
    
    switch (status) {
      case 'pending':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`}>
          Pending
        </span>;
      case 'approved':
        return <span className={`${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`}>
          Approved
        </span>;
      case 'denied':
        return <span className={`${baseClasses} bg-red-100 text-red-800  `}>
          Denied
        </span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`}>
          {status}
        </span>;
    }
  };

  const getTierBadge = (tier: string) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full';
    
    switch (tier) {
      case 'High Need':
        return <span className={`${baseClasses} bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200`}>
          High Need
        </span>;
      case 'Medium Need':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`}>
          Medium Need
        </span>;
      case 'Low Need':
        return <span className={`${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`}>
          Low Need
        </span>;
      case 'Ineligible':
        return <span className={`${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`}>
          Ineligible
        </span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`}>
          {tier}
        </span>;
    }
  };

  // Check for admin role
  if (!user || user.role !== 'admin') {
    return (
      <PageContainer>
        <PageSection title="Access Denied" emoji="🚫">
          <p>You must be an authorized administrator to view this page.</p>
        </PageSection>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <AdminNavigation />
      <PageSection title="Gift Request Review" emoji="🎁">
        <div className="space-y-6">
          {error && (
            <div className="bg-red-50 /20 p-4 rounded-lg">
              <p className="text-red-800 ">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <p>Loading pending requests...</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400">No pending gift requests to review.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div
                  key={request.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        User: {request.userId.substring(0, 8)}...
                      </span>
                      {getStatusBadge(request.status, request.escalationFlag)}
                      {getTierBadge(request.tier)}
                      {request.appealSubmitted && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                          Appeal
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(request.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Income:</span>
                      <span className="ml-2 text-gray-900 dark:text-gray-100">
                        {request.incomeRange.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Hardship:</span>
                      <span className="ml-2 text-gray-900 dark:text-gray-100">
                        {request.hardship ? request.hardship.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'None'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Streak:</span>
                      <span className="ml-2 text-gray-900 dark:text-gray-100">
                        {request.reflectionStreak} days
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Tokens:</span>
                      <span className="ml-2 text-gray-900 dark:text-gray-100">
                        {request.tokensAwarded.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {request.status === 'pending' && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedRequest(request)}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Review
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </PageSection>

      {/* Review Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4">Review Gift Request</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Review Note (Optional)</label>
              <textarea
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                rows={3}
                placeholder="Add a note about your decision..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  setReviewNote('');
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReview(selectedRequest.id, 'deny')}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Deny
              </button>
              <button
                onClick={() => handleReview(selectedRequest.id, 'approve')}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default GiftRequestReviewPage; 